import { z } from 'zod';
import { CallOutcome } from '../enums.js';
import { TimestampSchema } from '../primitives.js';
import { CallConfigMetaSchema, SessionTransport } from './callMeta.js';
import { CallerMessageSchema, MAX_TRANSCRIPT_TURNS, MAX_UNMET_REQUESTS, TranscriptTurnSchema, UnmetRequestSchema } from './call.js';
/**
 * Durable conversation lifecycle (doc 33 W2/W7, ADR-0011).
 *
 * Distinct identities, never interchangeable:
 *
 * - **Session id** — one phone or browser conversation. The Twilio/Telnyx
 *   CallSid for a phone call, `test-<uuid>` for a browser test. Stable across
 *   its tools and its reconciliation.
 * - **Admission id** — `<provider>_<providerCallId>`: carrier correlation, and
 *   the unit daily budgets are reserved and released against.
 * - **Booking intent id + version** — server-generated, owned by the session;
 *   a correction bumps the version and voids the old confirmation.
 * - **Operation id** — one durable mutation. Same id + same payload fingerprint
 *   replays the stored result; same id + different payload is a conflict.
 * - **Config hash** — the effective policy the conversation ran under.
 *
 * `callId` (= the session id) is lineage on an appointment, never an
 * idempotency key and never a selector for "cancel everything from this call"
 * (doc 32 F1).
 */
export const OperationKind = z.enum(['book', 'cancel', 'reschedule']);
/**
 * `businesses/{b}/operations/{operationId}` — one durable mutation result,
 * created in the SAME Firestore transaction as the appointment write it
 * describes. Server-only. A retry that lost its acknowledgement finds this row
 * and returns the original result instead of writing again (doc 33 W6 #5).
 */
export const OperationRecordSchema = z.object({
    kind: OperationKind,
    sessionId: z.string().min(1),
    intentId: z.string().nullable().default(null),
    intentVersion: z.number().int().nullable().default(null),
    /** sha256 of the canonical request payload. */
    fingerprint: z.string().min(1),
    /** Only committed results are stored: a transaction that aborted left no row. */
    state: z.literal('committed'),
    /** The tool-facing result, as returned the first time. */
    result: z.record(z.unknown()),
    appointmentIds: z.array(z.string()).default([]),
    test: z.boolean().default(false),
    createdAt: TimestampSchema,
});
export const SessionPhase = z.enum([
    /** Admitted and running. */
    'active',
    /** Close decided; settling pending work. */
    'closing',
    /** Final call record written. */
    'finalized',
    /** Shutdown/persistence could not finish: the reconciler must complete it. */
    'reconcile_required',
]);
/**
 * `businesses/{b}/sessions/{sessionId}` — the server-only lifecycle record a
 * conversation keeps while it runs. Kept APART from `calls/{id}` on purpose:
 * old portal builds and the old `onCallWritten` read every `calls` document as
 * a finished call, so an in-progress record must never appear there (doc 33
 * W13 #3). The reconciler turns an abandoned one into a call record.
 */
export const SessionRecordSchema = z.object({
    transport: SessionTransport,
    phase: SessionPhase,
    startedAt: TimestampSchema,
    updatedAt: TimestampSchema.optional(),
    endedAt: TimestampSchema.optional(),
    /** Caller ID (personal data; pruned with call records). */
    from: z.string().nullable().default(null),
    test: z.boolean().default(false),
    config: CallConfigMetaSchema.optional(),
    /** Bounded transcript checkpoint. */
    transcript: z.array(TranscriptTurnSchema).max(MAX_TRANSCRIPT_TURNS).default([]),
    transcriptTruncated: z.boolean().default(false),
    /** The caller's message, durable BEFORE the model is told it was taken. */
    message: CallerMessageSchema.nullable().default(null),
    /** Monotonic revision of `message`: an older write can never replace a newer one. */
    messageRev: z.number().int().min(0).default(0),
    unmetRequests: z.array(UnmetRequestSchema).max(MAX_UNMET_REQUESTS).default([]),
    /** Committed operation ids. */
    operationIds: z.array(z.string()).default([]),
    /** Operations that may or may not have committed (timed out, socket lost).
     * Never reported negative until the operation ledger says so. */
    uncertainOperationIds: z.array(z.string()).default([]),
    /** Server-owned booking intents (browser sessions, whose HTTP handlers are
     * stateless). Phone sessions keep these in memory. */
    intents: z.record(z.unknown()).default({}),
    /** Hard end of a browser model session, enforced server-side (doc 33 W10 #5). */
    deadlineAt: TimestampSchema.optional(),
    /** Provider-side handle (OpenAI WebRTC call id) for server-side hangup. */
    providerCallId: z.string().nullable().default(null),
    error: z.string().max(500).nullable().default(null),
    checkpointSeq: z.number().int().min(0).default(0),
});
/** What the business outcome is, separated from transport failure. */
export const CallOutcomeSummarySchema = z.object({
    outcome: CallOutcome,
    appointmentIds: z.array(z.string()).default([]),
    cancelledIds: z.array(z.string()).default([]),
});
export const AdmissionBudget = z.enum(['real', 'test', 'demo']);
export const AdmissionState = z.enum([
    /** Budget reserved, media not yet connected. */
    'admitted',
    /** A media socket (or browser model session) claimed it. */
    'connected',
    /** Conversation over. */
    'completed',
    /** Reservation given back (carrier failure before media). */
    'released',
    /** Browser token mint failed — reservation compensated. */
    'mint_failed',
]);
/**
 * `admissions/{provider}_{providerCallId}` (top level, server-only) — one
 * admitted conversation and the budget unit it spent (doc 33 W8).
 *
 * The reservation day is STORED, so a failure callback after midnight refunds
 * the day that was charged. A duplicate webhook finds this row and reuses its
 * decision instead of spending again; a release is a state transition that can
 * happen once. It is also the persisted call→business route for callbacks that
 * carry no diversion data.
 */
export const AdmissionSchema = z.object({
    provider: SessionTransport,
    providerCallId: z.string().min(1),
    businessId: z.string().min(1),
    /** Business-local `YYYY-MM-DD` the unit was charged to. */
    day: z.string(),
    budget: AdmissionBudget,
    /** Whether a usage counter was actually incremented (false when uncapped). */
    counted: z.boolean(),
    state: AdmissionState,
    /** At most one media owner, ever: reconnect is NOT supported (ADR-0011). */
    mediaClaim: z
        .object({ owner: z.string(), claimedAt: TimestampSchema })
        .nullable()
        .default(null),
    /** Concurrency permit expiry; renewed while the media session lives. */
    leaseUntil: TimestampSchema.optional(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.optional(),
});
export function admissionId(provider, providerCallId) {
    return `${provider}_${providerCallId}`.replace(/\//g, '_');
}
//# sourceMappingURL=session.js.map